import type { AssetGraphResponseDto } from '@terra/shared-contracts-external-network';

type AssetGraphNode = AssetGraphResponseDto['nodes'][number];

type ServicePort = {
  port: number;
  label: string;
};

const PORT_OWNER_KINDS = new Set<AssetGraphNode['kind']>(['HOST', 'GATEWAY']);

const toServicePort = (node: AssetGraphNode): ServicePort | null => {
  const { port, transportProtocol } = node.properties;

  if (node.kind !== 'NETWORK_SERVICE' || port === undefined) {
    return null;
  }

  return {
    port,
    label: transportProtocol ? `${port}/${transportProtocol}` : `${port}`,
  };
};

export const getOpenPortsByNodeId = (
  graph: AssetGraphResponseDto,
): Map<string, string[]> => {
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const portsByOwnerId = new Map<string, Map<string, ServicePort>>();

  for (const edge of graph.edges) {
    if (edge.type !== 'RUNS_SERVICE') continue;

    const source = nodesById.get(edge.source);
    const target = nodesById.get(edge.target);

    if (!source || !target) continue;

    const [owner, service] = PORT_OWNER_KINDS.has(source.kind)
      ? [source, target]
      : [target, source];
    const servicePort = toServicePort(service);

    if (!PORT_OWNER_KINDS.has(owner.kind) || !servicePort) continue;

    const ports =
      portsByOwnerId.get(owner.id) ?? new Map<string, ServicePort>();

    ports.set(servicePort.label, servicePort);
    portsByOwnerId.set(owner.id, ports);
  }

  return new Map(
    [...portsByOwnerId.entries()].map(([ownerId, ports]) => [
      ownerId,
      [...ports.values()]
        .sort(
          (left, right) =>
            left.port - right.port || left.label.localeCompare(right.label),
        )
        .map((servicePort) => servicePort.label),
    ]),
  );
};
